import { useEffect, useRef, useState, useCallback } from 'react'

interface Trail {
  id: number
  x: number
  y: number
  dark: boolean
}

let trailId = 0

const INTERACTIVE_TAGS = ['A', 'BUTTON', 'SELECT', 'TEXTAREA']

function isInteractive(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  if (INTERACTIVE_TAGS.includes(tag)) return true
  if (tag === 'INPUT') return true
  if (el.closest('a, button, [role="button"], label[for]')) return true
  const style = window.getComputedStyle(el)
  return style.cursor === 'pointer'
}

export default function CursorHeartbeat() {
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [idle, setIdle] = useState(false)
  const [onDark, setOnDark] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [trails, setTrails] = useState<Trail[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null)
  const lastTrailRef = useRef(0)

  const addTrail = useCallback((x: number, y: number, dark: boolean) => {
    const now = performance.now()
    if (now - lastTrailRef.current < 60) return
    lastTrailRef.current = now

    const id = ++trailId
    setTrails((prev) => [...prev.slice(-12), { id, x, y, dark }])
    setTimeout(() => setTrails((prev) => prev.filter((t) => t.id !== id)), 500)
  }, [])

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      setPos({ x: e.clientX, y: e.clientY })
      setIdle(false)

      const el = document.elementFromPoint(e.clientX, e.clientY)
      const dark = !!el?.closest('.bg-espresso')
      const interactive = isInteractive(el)
      setOnDark(dark)
      setHovering(interactive)

      if (!interactive) {
        addTrail(e.clientX, e.clientY, dark)
      }

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setIdle(true), 1500)
    }

    const handleLeave = () => {
      setIdle(false)
      setHovering(false)
      if (timerRef.current) clearTimeout(timerRef.current)
    }

    window.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseleave', handleLeave)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseleave', handleLeave)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [addTrail])

  const cursorSrc = onDark ? '/assets/cursor-heart-sand.png' : '/assets/cursor-heart-espresso.png'

  return (
    <>
      {!hovering &&
        trails.map((t) => (
          <div
            key={t.id}
            className="pointer-events-none fixed z-[9998] animate-cursor-trail"
            style={{ left: t.x, top: t.y, transform: 'translate(-16px, -16px)' }}
          >
            <img
              src={t.dark ? '/assets/cursor-heart-sand.png' : '/assets/cursor-heart-espresso.png'}
              alt=""
              width={32}
              height={32}
              className="block"
            />
          </div>
        ))}
      {idle && !hovering && (
        <div
          className="pointer-events-none fixed z-[9999]"
          style={{ left: pos.x, top: pos.y, transform: 'translate(-16px, -16px)' }}
        >
          <div className="animate-heartbeat">
            <img src={cursorSrc} alt="" width={32} height={32} className="block" />
          </div>
        </div>
      )}
    </>
  )
}
